/**
 * Hook that conditionally triggers loadFileTypeObjects — guarded by isFilterActive.
 *
 * When a search is active (isFilterActive=true) the tree is driven entirely by
 * search results. Calling loadFileTypeObjects in that state would overwrite the
 * filtered tree with emptyModelTree, breaking mobile search where the panel
 * mounts fresh after results arrive.
 */
import { Dispatch, MutableRefObject, SetStateAction, useEffect } from "react";

import { Tree } from "../../../components/objectTree/types";
import { ModelTreeFilter, ModelTreeNodeData } from "../types";

export interface UseFileTypeLoadGuardParams {
    loadFileTypeObjects: (params: {
        fileTypes: ModelTreeFilter[];
        setTree: Dispatch<SetStateAction<Tree<ModelTreeNodeData>>>;
        abortSignal: AbortSignal;
    }) => void;
    fileTypes: ModelTreeFilter[];
    setTree: Dispatch<SetStateAction<Tree<ModelTreeNodeData>>>;
    filterAbortController: MutableRefObject<AbortController>;
    isFilterActive: boolean;
}

export function useFileTypeLoadGuard({
    loadFileTypeObjects,
    fileTypes,
    setTree,
    filterAbortController,
    isFilterActive,
}: UseFileTypeLoadGuardParams) {
    useEffect(() => {
        // When a search is active, the tree is driven by search results — skip
        // file-type loading which would overwrite the filtered tree with emptyModelTree.
        if (isFilterActive) {
            return;
        }
        loadFileTypeObjects({
            fileTypes,
            setTree,
            abortSignal: filterAbortController.current.signal,
        });
    }, [loadFileTypeObjects, fileTypes, filterAbortController, setTree, isFilterActive]);
}
